/**
 * Fields for a template's `input` variables — values staff type when they send
 * (or start a form), passed to the API as `variable_inputs`.
 */

import React from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { RequiredInput } from './api';

interface VariableInputFieldsProps {
  inputs: RequiredInput[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
  idPrefix?: string;
}

const fieldClass =
  'w-full rounded-md border border-v2border bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-v2accent';

/** Required inputs that are still empty. */
export function missingRequiredInputs(inputs: RequiredInput[], values: Record<string, string>): RequiredInput[] {
  return inputs.filter((i) => i.required && !(values[i.key] ?? '').toString().trim() && !i.default_value);
}

export function VariableInputFields({ inputs, values, onChange, disabled, idPrefix = 'tv-input' }: VariableInputFieldsProps) {
  const { t } = useAppTranslation();
  if (!inputs || inputs.length === 0) return null;

  const set = (key: string, value: string) => onChange({ ...values, [key]: value });

  return (
    <fieldset className="space-y-3 rounded-md border border-v2border bg-paper-2 p-3" data-testid="variable-input-fields">
      <legend className="px-1 text-xs font-semibold text-ink-700">
        {t('templateVariables.inputs.title', 'Values for this message')}
      </legend>
      {inputs.map((input) => {
        const id = `${idPrefix}-${input.key}`;
        const value = values[input.key] ?? '';
        const placeholder = input.default_value ?? undefined;
        let control: React.ReactNode;
        switch (input.value_type) {
          case 'select':
            control = (
              <select id={id} className={fieldClass} value={value} disabled={disabled} onChange={(e) => set(input.key, e.target.value)}>
                <option value="">{t('templateVariables.inputs.choose', 'Choose…')}</option>
                {(input.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            );
            break;
          case 'boolean':
            control = (
              <select id={id} className={fieldClass} value={value} disabled={disabled} onChange={(e) => set(input.key, e.target.value)}>
                <option value="">{t('templateVariables.inputs.choose', 'Choose…')}</option>
                <option value="yes">{t('templateVariables.inputs.yes', 'Yes')}</option>
                <option value="no">{t('templateVariables.inputs.no', 'No')}</option>
              </select>
            );
            break;
          case 'date':
            control = <input id={id} type="date" className={fieldClass} value={value} disabled={disabled} onChange={(e) => set(input.key, e.target.value)} />;
            break;
          case 'datetime':
            control = (
              <input id={id} type="datetime-local" className={fieldClass} value={value} disabled={disabled} onChange={(e) => set(input.key, e.target.value)} />
            );
            break;
          case 'number':
            control = (
              <input id={id} type="number" className={fieldClass} value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => set(input.key, e.target.value)} />
            );
            break;
          case 'link':
            control = (
              <input id={id} type="url" className={fieldClass} value={value} disabled={disabled} placeholder={placeholder ?? 'https://'} onChange={(e) => set(input.key, e.target.value)} />
            );
            break;
          default:
            control = (
              <input id={id} type="text" className={fieldClass} value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => set(input.key, e.target.value)} />
            );
        }
        return (
          <div key={input.key}>
            <label htmlFor={id} className="mb-1 block text-xs font-medium text-ink-700">
              {input.label || input.key}
              {input.required && <span className="text-danger"> *</span>}
              <code className="ml-2 font-mono text-[11px] text-ink-500">{`{{${input.key}}}`}</code>
            </label>
            {input.prompt && <p className="mb-1 text-[11px] text-ink-500">{input.prompt}</p>}
            {control}
          </div>
        );
      })}
    </fieldset>
  );
}
